import { NAV_SECTIONS, ADMIN_NAV_SECTIONS, ROUTES, IS_DEV } from '../../constants/constants';
import { DOCUMENTS } from '../../data/documents';
import { RESOLUTIONS } from '../../data/resolutions';
import { MCA_FORMS } from '../../data/mcaForms';
import { QUICK_ACTIONS, ADMIN_QUICK_ACTIONS } from '../../data/quickActions';
import { REPORT_TEMPLATES } from '../../data/reportTemplates';

/* ------------------------------------------------------------------ */
/*  Build a flat, pre-indexed array of every searchable item.         */
/*  Each item has: id, category, label, subtitle, icon, keywords,     */
/*  route, action.  ~200 items total — search is O(n), instant.       */
/* ------------------------------------------------------------------ */

function buildIndex(effectiveRoles) {
  const items = [];
  const isAdmin = effectiveRoles.includes('admin') || effectiveRoles.includes('super_admin');

  // 1. Quick Actions (admin gets admin-specific actions, users get user-facing ones)
  const quickActionsList = isAdmin ? ADMIN_QUICK_ACTIONS : QUICK_ACTIONS;
  quickActionsList.forEach((qa) => {
    items.push({
      id: qa.id,
      category: 'quick-action',
      label: qa.label,
      subtitle: '',
      icon: qa.icon,
      keywords: [qa.label.toLowerCase()],
      route: qa.actionType === 'navigate' ? qa.target : null,
      action: qa.actionType === 'modal' ? { type: 'modal', target: qa.target } : null,
    });
  });

  // 2. Navigation pages (role-filtered)
  const navSections = isAdmin ? ADMIN_NAV_SECTIONS : NAV_SECTIONS;
  navSections
    .filter((s) => !s.devOnly || IS_DEV)
    .forEach((section) => {
      section.items
        .filter((item) =>
          item.roles.some((r) => effectiveRoles.includes(r)) && (!item.devOnly || IS_DEV),
        )
        .forEach((item) => {
          items.push({
            id: `nav-${item.id}`,
            category: 'page',
            label: item.label,
            subtitle: section.label,
            icon: item.icon,
            keywords: [item.label.toLowerCase(), section.label.toLowerCase(), item.id],
            route: item.route,
            action: null,
          });
        });
    });

  // 3–6: User-facing content (skip for admin users)
  if (!isAdmin) {

  // 3. Documents
  DOCUMENTS.forEach((doc) => {
    items.push({
      id: `doc-${doc.id}`,
      category: 'document',
      label: doc.title,
      subtitle: doc.section || doc.tag,
      icon: doc.icon,
      keywords: [
        doc.title.toLowerCase(),
        (doc.section || '').toLowerCase(),
        (doc.tag || '').toLowerCase(),
        doc.id,
      ],
      route: ROUTES.DOC_LIBRARY,
      action: null,
      _navState: { documentId: doc.id },
    });
  });

  // 4. Resolutions
  RESOLUTIONS.forEach((res) => {
    items.push({
      id: `res-${res.id}`,
      category: 'resolution',
      label: res.title,
      subtitle: res.subtitle || res.sectionTitle || '',
      icon: res.icon,
      keywords: [
        res.title.toLowerCase(),
        (res.subtitle || '').toLowerCase(),
        (res.sectionTitle || '').toLowerCase(),
        res.id,
      ],
      route: ROUTES.RESOLUTIONS,
      action: null,
      _navState: { resolutionId: res.id },
    });
  });

  // 5. MCA Forms
  MCA_FORMS.forEach((f) => {
    items.push({
      id: `mca-${f.id}`,
      category: 'mca-form',
      label: f.form,
      subtitle: f.title,
      icon: '📑',
      keywords: [
        f.form.toLowerCase(),
        f.title.toLowerCase(),
        (f.who || '').toLowerCase(),
        f.id,
        (f.category || '').toLowerCase(),
      ],
      route: ROUTES.MCA_FORMS,
      action: null,
      _navState: { search: f.form },
    });
  });

  // 6. Report Templates
  REPORT_TEMPLATES.forEach((rt) => {
    items.push({
      id: `report-${rt.id}`,
      category: 'report-template',
      label: rt.title,
      subtitle: rt.section || rt.tag,
      icon: rt.icon,
      keywords: [
        rt.title.toLowerCase(),
        (rt.section || '').toLowerCase(),
        (rt.description || '').toLowerCase(),
        ...(rt.tags || []).map((t) => t.toLowerCase()),
        rt.id,
      ],
      route: ROUTES.REPORT_TEMPLATES,
      action: null,
      _navState: { reportId: rt.id },
    });
  });

  // 6b. Report Generator — form-driven statutory report builder
  items.push({
    id: 'report-generator-auditor',
    category: 'page',
    label: "Independent Auditor's Report — Generator",
    subtitle: 'Report Generator',
    icon: '🧾',
    keywords: [
      "independent auditor's report", 'auditor report', 'audit report', 'report generator',
      'caro 2020', 'sa 700', 'ifc report', '143(3)(i)', 'auditor-report',
    ],
    route: ROUTES.REPORT_GENERATOR,
    action: null,
    _navState: { reportTypeId: 'auditor-report' },
  });

  } // end !isAdmin guard

  // 7. Admin-specific deep keywords (admin/super_admin only)
  if (effectiveRoles.includes('admin') || effectiveRoles.includes('super_admin')) {
    const adminKeywords = [
      { id: 'admin-kw-api-key',       label: 'Manage API Keys',          route: ROUTES.ADMIN_AI_PROVIDERS, keywords: ['api key', 'openai key', 'anthropic key', 'gemini key', 'ai configuration'] },
      { id: 'admin-kw-default-model', label: 'Set Default AI Model',     route: ROUTES.ADMIN_AI_PROVIDERS, keywords: ['default model', 'model selection', 'gpt', 'claude'] },
      { id: 'admin-kw-test-model',    label: 'Test AI Model Connection', route: ROUTES.ADMIN_AI_PROVIDERS, keywords: ['test model', 'model test', 'connection test'] },
      { id: 'admin-kw-usage-cost',    label: 'AI Cost Analytics',        route: ROUTES.ADMIN_AI_USAGE_COST, keywords: ['ai cost', 'token usage', 'api cost', 'billing', 'usage analytics'] },
      { id: 'admin-kw-add-service',   label: 'Add New Service',          route: ROUTES.ADMIN_SERVICES,  keywords: ['add service', 'create service', 'new service'] },
      { id: 'admin-kw-permissions',   label: 'Manage Permissions',       route: ROUTES.ADMIN_ROLES,     keywords: ['permissions', 'access control', 'role permissions'] },
      { id: 'admin-kw-create-role',   label: 'Create New Role',          route: ROUTES.ADMIN_ROLES,     keywords: ['create role', 'add role', 'new role'] },
      { id: 'admin-kw-companies',    label: 'Company Management',       route: ROUTES.ADMIN_COMPANIES, keywords: ['all companies', 'registered companies', 'company management', 'cin lookup'] },
      { id: 'admin-kw-orders',       label: 'Order Management',         route: ROUTES.ADMIN_ORDERS,    keywords: ['all orders', 'order history', 'order management', 'order lookup'] },
    ];

    adminKeywords.forEach((ak) => {
      items.push({
        id: ak.id,
        category: 'page',
        label: ak.label,
        subtitle: 'Administration',
        icon: '\u2699\uFE0F',
        keywords: [ak.label.toLowerCase(), ...ak.keywords],
        route: ak.route,
        action: null,
      });
    });
  }

  return items;
}

/* ------------------------------------------------------------------ */
/*  Scoring: tokenised substring matching, ranked by relevance.       */
/* ------------------------------------------------------------------ */

function scoreItem(item, tokens) {
  let score = 0;
  const label = item.label.toLowerCase();

  for (const token of tokens) {
    // Exact label start → highest
    if (label.startsWith(token)) {
      score += 100;
    } else if (label.includes(token)) {
      score += 60;
    }

    // Keyword match
    for (const kw of item.keywords) {
      if (kw.includes(token)) {
        score += 30;
        break; // one bonus per token
      }
    }
  }

  return score;
}

/* ------------------------------------------------------------------ */
/*  Public API                                                        */
/* ------------------------------------------------------------------ */

const CATEGORY_ORDER = ['quick-action', 'page', 'company', 'document', 'resolution', 'report-template', 'mca-form'];
const CATEGORY_LABELS = {
  'quick-action': 'Quick Actions',
  page: 'Pages',
  company: 'Companies',
  document: 'Documents',
  resolution: 'Resolutions',
  'report-template': 'Report Templates',
  'mca-form': 'MCA Forms',
};
const MAX_PER_CATEGORY = 5;

/**
 * Create a search instance bound to the user's roles.
 * Returns { search(query), getQuickActions() }.
 */
export function createSearchIndex(effectiveRoles) {
  const index = buildIndex(effectiveRoles);

  function search(query, companies = []) {
    const raw = (query || '').trim().toLowerCase();
    if (!raw) return [];

    const tokens = raw.split(/\s+/).filter(Boolean);

    // Score all static items
    const scored = [];
    for (const item of index) {
      const s = scoreItem(item, tokens);
      if (s > 0) scored.push({ ...item, _score: s });
    }

    // Score companies from context (client-side)
    for (const co of companies) {
      const name = String(co.name || '').toLowerCase();
      const cin = String(co.cin || '').toLowerCase();
      let s = 0;
      for (const token of tokens) {
        if (name.startsWith(token)) s += 100;
        else if (name.includes(token)) s += 60;
        if (cin.includes(token)) s += 40;
      }
      if (s > 0) {
        scored.push({
          id: `co-${co.cin || co._id}`,
          category: 'company',
          label: co.name || 'Unnamed',
          subtitle: co.cin || '',
          icon: '🏢',
          keywords: [],
          // Selecting a company opens the view/edit details modal in-place
          // (where it can also be set as the active workspace) — no navigation.
          action: { type: 'companyDetails', target: co },
          _score: s,
        });
      }
    }

    // Sort descending by score
    scored.sort((a, b) => b._score - a._score);

    // Group by category, limit per group
    const groups = [];
    const countByCategory = {};

    for (const item of scored) {
      const cat = item.category;
      countByCategory[cat] = (countByCategory[cat] || 0) + 1;
      if (countByCategory[cat] > MAX_PER_CATEGORY) continue;

      let group = groups.find((g) => g.category === cat);
      if (!group) {
        group = { category: cat, label: CATEGORY_LABELS[cat] || cat, items: [] };
        groups.push(group);
      }
      group.items.push(item);
    }

    // Sort groups by defined order
    groups.sort(
      (a, b) => CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category),
    );

    return groups;
  }

  function getQuickActions() {
    return index.filter((item) => item.category === 'quick-action');
  }

  return { search, getQuickActions };
}
